import { useEffect, useState } from 'react';
import type { Entity } from '@backstage/catalog-model';
import { useApi } from '@backstage/frontend-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

/**
 * The groups of Aan de slag, in the order a team meets them: start something, meet the
 * standards, keep the quality up, organise the people. A template says where it belongs with
 * its spec.type; within a group the first names come first, the rest by title.
 */
type TemplateGroup = { type: string; title: string; text: string; first: readonly string[] };

export const TEMPLATE_GROUPS: readonly TemplateGroup[] = [
  {
    type: 'nieuw',
    title: 'Iets nieuws beginnen',
    text: 'Een nieuwe repository op code.overheid.nl, met de standaarden er al in.',
    first: ['overheidsdienst'],
  },
  {
    type: 'standaarden',
    title: 'Voldoen aan de standaarden',
    text: 'Een pull request op een bestaande repository met wat de standaarden vragen.',
    first: ['open-source-gereed'],
  },
  {
    type: 'kwaliteit',
    title: 'Kwaliteit bewaken',
    text: 'Controles die voortaan bij elke pull request meekijken.',
    first: [],
  },
  {
    type: 'team',
    title: 'Samenwerken',
    text: 'Een team of organisatie op code.overheid.nl, met jou erin.',
    first: ['nieuw-team'],
  },
];

const OTHER = {
  type: '',
  title: 'Overig',
  text: '',
  first: [] as readonly string[],
};

const href = (entity: Entity) =>
  `/create/templates/${entity.metadata.namespace ?? 'default'}/${
    entity.metadata.name
  }`;

/** Puts the templates in their groups; a type no group knows lands under Overig. */
export function groupTemplates(templates: Entity[]) {
  const known = new Set<string>(TEMPLATE_GROUPS.map(group => group.type));
  return [...TEMPLATE_GROUPS, OTHER]
    .map(group => {
      const items = templates.filter(entity => {
        const type = String(entity.spec?.type ?? '');
        return group === OTHER ? !known.has(type) : type === group.type;
      });
      const rank = (entity: Entity) => {
        const index = group.first.indexOf(entity.metadata.name);
        return index === -1 ? group.first.length : index;
      };
      items.sort(
        (a, b) =>
          rank(a) - rank(b) ||
          (a.metadata.title ?? a.metadata.name).localeCompare(
            b.metadata.title ?? b.metadata.name,
            'nl',
          ),
      );
      return { ...group, items };
    })
    .filter(group => group.items.length);
}

export function TemplateGroups() {
  const catalogApi = useApi(catalogApiRef);
  const [templates, setTemplates] = useState<Entity[]>();
  const [failure, setFailure] = useState<string>();

  useEffect(() => {
    catalogApi
      .getEntities({
        filter: { kind: 'Template' },
        fields: ['kind', 'metadata', 'spec.type'],
      })
      .then(({ items }) => setTemplates(items))
      .catch(error => setFailure(String(error?.message ?? error)));
  }, [catalogApi]);

  if (failure) {
    return (
      <nldd-simple-section>
        <nldd-banner
          variant="critical"
          text="De catalogus is nu niet beschikbaar"
          supporting-text={failure}
        />
      </nldd-simple-section>
    );
  }
  if (!templates) {
    return (
      <nldd-simple-section>
        <nldd-text size="sm" color="secondary">
          Wordt opgehaald
        </nldd-text>
      </nldd-simple-section>
    );
  }
  return (
    <nldd-simple-section>
      <nldd-container layout="stack" gap="32">
        {groupTemplates(templates).map(group => (
          <section key={group.title} aria-label={group.title}>
            <nldd-title
              size="4"
              heading-level="2"
              text={group.title}
              supporting-text={group.text || undefined}
            />
            <nldd-spacer size="8" />
            <nldd-list
              appearance="box-base"
              type="navigation"
              accessible-label={group.title}
            >
              {group.items.map(entity => (
                <nldd-list-item
                  key={entity.metadata.uid ?? entity.metadata.name}
                  href={href(entity)}
                >
                  <nldd-title-cell
                    text={entity.metadata.title ?? entity.metadata.name}
                    supporting-text={entity.metadata.description}
                  />
                </nldd-list-item>
              ))}
            </nldd-list>
          </section>
        ))}
      </nldd-container>
    </nldd-simple-section>
  );
}
